import { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { generateHTML } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/lib/blogPosts";
import { fetchBlogPost } from "@/services/seoService";
import { isReleaseFlagOn } from '@/config/featureFlags';

/* ── TipTap rendered body ── */
function TipTapBody({ body }: { body: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    let html: string;
    try {
      // body may be TipTap JSON or raw HTML string
      const parsed = JSON.parse(body) as object;
      html = generateHTML(parsed, [StarterKit, Image, TiptapLink]);
    } catch {
      html = body;
    }
    ref.current.innerHTML = DOMPurify.sanitize(html);
  }, [body]);

  return (
    <div
      ref={ref}
      className="prose prose-indigo max-w-none text-[#262A7A] leading-relaxed"
    />
  );
}

/* ── API mode ── */
function ApiBlogPostPage() {
  // We accept both :slug (new) and :id (legacy) — App.tsx sets :slug
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => fetchBlogPost(slug ?? ''),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (data === 'GONE') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Helmet>
          <title>Article Removed | BookMyCoaching</title>
        </Helmet>
        <Header />
        <div className="flex-1 flex items-center justify-center py-24 px-4">
          <div className="bg-[#EEEFF9] rounded-xl p-10 text-center max-w-md w-full">
            <p className="text-lg text-[#5A5E80] mb-4">This article is no longer available.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors duration-150 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (data === null || data === undefined) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Helmet>
          <title>Post Not Found | BookMyCoaching</title>
        </Helmet>
        <Header />
        <div className="flex-1 flex items-center justify-center py-24">
          <div className="text-center">
            <p className="text-lg text-[#5A5E80] mb-4">Article not found.</p>
            <Link to="/blog">
              <Button variant="outline" className="cursor-pointer">Back to Blog</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const post = data;
  const pageTitle = post.ogTitle ?? `${post.title} | BookMyCoaching`;
  const pageDescription = post.ogDescription ?? post.excerpt ?? '';
  const canonicalUrl = getCanonicalUrl(`/blog/${post.slug}`);
  const ogImage = post.ogImageUrl ?? post.featuredImage ?? getDefaultOgImage();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pb-16 lg:pt-44 bg-gradient-to-b from-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors duration-150 mt-4 mb-6 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag) => (
                  <span key={tag} className="inline-block px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1D2045] mb-4">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-lg text-[#5A5E80] mb-6">{post.excerpt}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-[#5A5E80]">
              {post.authorName && (
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  {post.authorName}
                </div>
              )}
              {post.publishedAt && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.publishedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured image */}
      {post.featuredImage && (
        <div className="w-full max-h-96 overflow-hidden">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Body */}
      {post.body && (
        <section className="py-10 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <TipTapBody body={post.body} />
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-12 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Find Your Perfect Teacher?</h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            Start your learning journey today and connect with verified teachers who match your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="secondary" className="cursor-pointer">Explore Teachers</Button>
            </Link>
            <Link to="/teacher-profile-setup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 cursor-pointer">
                Become a Teacher
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ── Static mode ── */
function StaticBlogPostPage() {
  // Support both :slug (new App.tsx route) and :id (legacy/test route)
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const postId = slug ?? id;
  const post = blogPosts.find((p) => p.id === postId);

  const blogHelmet = (
    <Helmet>
      <title>{post ? `${post.title} | BookMyCoaching` : 'Post Not Found | BookMyCoaching'}</title>
      <meta name="description" content={post?.excerpt ?? ''} />
      <link rel="canonical" href={getCanonicalUrl(`/blog/${postId}`)} />
      <meta property="og:title" content={post?.title ?? 'BookMyCoaching Blog'} />
      <meta property="og:description" content={post?.excerpt ?? ''} />
      <meta property="og:url" content={getCanonicalUrl(`/blog/${postId}`)} />
      <meta property="og:image" content={post?.image ?? getDefaultOgImage()} />
    </Helmet>
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {blogHelmet}
        <Header />
        <div className="flex-1 flex items-center justify-center py-24">
          <div className="text-center">
            <p className="text-lg text-[#5A5E80] mb-4">Article not found.</p>
            <Link to="/blog">
              <Button variant="outline" className="cursor-pointer">Back to Blog</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const related = blogPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  const paragraphs = post.content.split("\n\n");

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {blogHelmet}
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pb-16 lg:pt-44 bg-gradient-to-b from-indigo-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 mt-4 mb-6 transition-colors duration-150 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <span className="inline-block px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full mb-4 ml-4">
              {post.category}
            </span>

            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1D2045] mb-4">
              {post.title}
            </h1>

            <p className="text-lg text-[#5A5E80] mb-6">{post.excerpt}</p>

            <div className="flex items-center gap-4 text-sm text-[#5A5E80]">
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.date}
              </div>
              <div>{post.readTime}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Image */}
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto -mt-4 sm:-mt-6">
          <div className="h-64 sm:h-96 rounded-xl overflow-hidden bg-[#EEEFF9]">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-10 sm:py-14">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-5">
            {paragraphs.map((paragraph, i) => (
              <p key={i} className="text-base leading-relaxed text-[#262A7A]">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-10 sm:py-14 bg-[#F7F7FC] border-t border-[#DCDDEE]">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-[#1D2045] mb-6">More in {post.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.id}`}
                    className="block bg-white rounded-lg border border-[#DCDDEE] overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                  >
                    <div className="h-36 bg-[#EEEFF9] overflow-hidden">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-[#1D2045] mb-2 line-clamp-2">{r.title}</h3>
                      <div className="flex items-center justify-between text-xs text-[#5A5E80]">
                        <span>{r.readTime}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-indigo-600" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-12 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Find Your Perfect Teacher?</h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            Start your learning journey today and connect with verified teachers who match your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="secondary" className="cursor-pointer">Explore Teachers</Button>
            </Link>
            <Link to="/teacher-profile-setup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 cursor-pointer">
                Become a Teacher
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ── Entry point ── */
export default function BlogPostPage() {
  const flagOn = isReleaseFlagOn('SEO_PANEL');
  return flagOn ? <ApiBlogPostPage /> : <StaticBlogPostPage />;
}
